import { useRef } from "react";
import Webcam from "react-webcam";
import { useSecureCamera } from "./useSecureCamera";
import * as LucideIcons from "lucide-react";

// Frame configurations matching the SDK
const FRAME_CONFIGS = {
  ID: { aspectRatio: 1.585, width: "min(85%, 380px)", borderRadius: "20px" },
  PASSPORT: { aspectRatio: 0.707, width: "min(75%, 300px)", borderRadius: "12px" }, // open passport, both pages, upright (ICAO TD3, 125 x 176 mm)
  SELFIE: { aspectRatio: 1, width: "min(70%, 280px)", borderRadius: "50%" },
  NONE: { aspectRatio: 1, width: "0%", borderRadius: "0px" }
};

const SecureCamera = ({
  isOpen,
  onClose,
  onCapture,
  frameType = "ID",
  theme = {},
  title
}) => {
  const { 
    videoRef, 
    isFake,
    isReady,
    error,
    capture, 
    checkSecurity,
    handleCameraError,
    stopStream,
    videoConstraints,
    useFallbackConstraints,
    preferredDeviceId,
  } = useSecureCamera({
    security: { blockVirtualDevices: true, requireHardwareFeatures: true }
  });

  const lastFocusKickRef = useRef(0);

  const {
    primaryColor = "#ffffff",
    overlayColor = "rgba(0,0,0,0.7)",
    guideColor = "#ffffff",
    fontFamily = "inherit",
    textColor = "#ffffff"
  } = theme;

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const triggerFocus = async (track, force = false) => {
    if (!track?.getCapabilities || !track?.applyConstraints) return;

    const now = Date.now();
    if (!force && now - lastFocusKickRef.current < 700) return;
    lastFocusKickRef.current = now;

    try {
      const capabilities = track.getCapabilities?.() || {};
      const modes = capabilities.focusMode || [];

      if (!modes.length) return;

      if (modes.includes("single-shot")) {
        await track.applyConstraints({ advanced: [{ focusMode: "single-shot" }] });

        if (modes.includes("continuous")) {
          setTimeout(() => {
            track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
          }, 200);
        }
        return;
      }

      if (modes.includes("continuous")) {
        const breakMode = modes.includes("auto")
          ? "auto"
          : modes.includes("manual")
          ? "manual"
          : null;

        if (breakMode) {
          await track.applyConstraints({ advanced: [{ focusMode: breakMode }] });
        }

        setTimeout(() => {
          track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
        }, 220);
      }
    } catch (err) {
      console.warn("Focus trigger failed:", err);
    }
  };

  const handleUserMedia = async (stream) => {
    const result = await checkSecurity(stream);
    const track = stream?.getVideoTracks?.()?.[0];
    if (track && result !== false) {
      triggerFocus(track);
      setTimeout(() => {
        if (track.readyState === "live") triggerFocus(track);
      }, 600);
    }
    return result;
  };

  const handleCapture = async () => {
    const track = videoRef.current?.stream?.getVideoTracks?.()?.[0];
    if (track) {
      await triggerFocus(track, true);
      // await sleep(320);
      await sleep(1000);
    }
    const result = await capture();
    if (result) {
      onCapture(result);
    }
  };

  if (!isOpen) return null;

  const config = FRAME_CONFIGS[frameType] || FRAME_CONFIGS.ID;

  return (
    <>
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: '#000',
        fontFamily,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {isFake ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', textAlign: 'center' }}>
             <h2 style={{ color: '#FF3B30' }}>Security Alert</h2>
             <p style={{ color: '#ccc' }}>Virtual device detected. Please use a physical camera.</p>
             <button onClick={onClose} style={{ marginTop: '20px', padding: '12px 24px', borderRadius: '8px', border: 'none', background: primaryColor, color: '#000' }}>Close</button>
          </div>
        ) : (isReady && error === "permission_denied") ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', textAlign: 'center' }}>
             <div style={{ marginBottom: '20px' }}>
               <LucideIcons.ShieldOff size={48} color="#FF9500" />
             </div>
             <h2 style={{ color: textColor, marginBottom: '10px' }}>Camera permission required</h2>
             <p style={{ color: '#ccc', marginBottom: '20px' }}>Please allow camera access in your browser settings, then try again.</p>
             <button onClick={onClose} style={{ padding: '12px 24px', borderRadius: '8px', border: 'none', background: primaryColor, color: '#000' }}>Close</button>
          </div>
        ) : (isReady && error) ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', textAlign: 'center' }}>
             <div style={{ marginBottom: '20px' }}>
               <LucideIcons.CameraOff size={48} color="#FF3B30" />
             </div>
             <h2 style={{ color: textColor, marginBottom: '10px' }}>Unable to access camera</h2>
             <p style={{ color: '#ccc', marginBottom: '20px' }}>Please close this screen and try again.</p>
             <button onClick={onClose} style={{ padding: '12px 24px', borderRadius: '8px', border: 'none', background: primaryColor, color: '#000' }}>Close</button>
          </div>
        ) : (
          <>
          <div
            style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <Webcam
              key={`${useFallbackConstraints ? "fallback" : "highres"}-${preferredDeviceId || "auto"}`}
                audio={false}
                autoPlay={true}
                ref={videoRef}
                screenshotFormat="image/jpeg"
                onUserMedia={handleUserMedia}
                onUserMediaError={handleCameraError}
                videoConstraints={videoConstraints}
                forceScreenshotSourceSize={true}
                screenshotQuality={1}
                playsInline={true}
              muted={true}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />

            {!isReady ? (
              <div style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: '#000',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                textAlign: 'center',
                zIndex: 10
              }}>
                <div style={{ marginBottom: '20px' }}>
                  <LucideIcons.Camera size={48} color={primaryColor} />
                </div>
                <h2 style={{ color: textColor, marginBottom: '10px' }}>Loading camera...</h2>
                <p style={{ color: '#ccc' }}>Please wait while we initialize your camera</p>
              </div>
            ) : (
              <div style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: config.width,
                  aspectRatio: config.aspectRatio.toString(),
                  borderRadius: config.borderRadius,
                  border: `3px solid ${guideColor}`,
                  boxShadow: `0 0 0 100vmax ${overlayColor}`,
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}>
                  {title && (
                    <div style={{
                      position: 'absolute',
                      top: -60,
                      width: '100%',
                      textAlign: 'center',
                      color: textColor,
                      fontWeight: 700,
                      fontSize: '18px',
                      textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                    }}>
                      {title}
                    </div>
                  )}
                </div>
              </div>
            )}

            </div>

            <div style={{
              padding: '40px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
              position: 'absolute',
              bottom: 0,
              width: '100%'
            }}>
              <button onClick={() => { stopStream(); onClose(); }} style={{ position: 'absolute', left: 30, background: 'none', border: 'none', color: '#fff' }}>
                <LucideIcons.X size={32} />
              </button>

              <button 
                onClick={handleCapture}
                disabled={isFake || !!error || !isReady}
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  backgroundColor: primaryColor,
                  border: `6px solid rgba(255,255,255,0.2)`,
                  cursor: (isFake || !!error || !isReady) ? 'not-allowed' : 'pointer',
                  opacity: (isFake || !!error || !isReady) ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 20px rgba(0,0,0,0.5)'
                }}
              >
                 <div style={{ width: 50, height: 50, borderRadius: '50%', border: '2px solid rgba(0,0,0,0.1)' }} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SecureCamera;
